import { AA_NORMAL_TEXT, contrastRatio, formatRatio } from '@/lib/contrast';
import type {
  AnimationSpeed,
  BlockAnimation,
  ButtonShape,
  ButtonStyle,
  ExperienceFont,
  ExperienceScale,
  ExperienceTheme,
  PageTransitionAnimation,
  ResolvedExperienceTheme,
} from '@matter_extractor/shared';
import { resolveTheme } from '@matter_extractor/shared';

/**
 * Ce que la barre « Visuel global » DIT, et ce qu'elle mesure (refonte
 * modulaire §9.4 ; maquettes `Timeline.dc.html` et `BarreDeTheme.dc.html` ;
 * issue #57).
 *
 * Pure, and written before the bar that reads it, for the reason
 * `experience-labels.ts` gives at its own head: every one of these strings is
 * read TWICE - once by the collapsed summary, once by the open panel, and the
 * contrast sentences a third time by the save box of §9.1 - and a sentence
 * worded twice is worded two ways.
 *
 * ## What is NOT here: the merge
 *
 * `resolveTheme` is in `shared` and is the only merge (§9.4: « trois niveaux de
 * surcharge, tous partiels et fusionnés dans l'ordre »). What this module adds
 * is the two questions a merge cannot answer because it deliberately forgets
 * them: WHICH level a token came from - « hérité » against « surchargé » - and
 * what the resulting pairing measures.
 *
 * ## The contrast WARNS, it never refuses
 *
 * §9.4 in as many words: « elle ne refuse jamais : c'est un outil pour des gens
 * de l'équipe, pas un portail de conformité, et un gris volontairement pâle sur
 * un intertitre est un choix, pas une faute ». So nothing here answers a
 * boolean the save could gate on; it answers SENTENCES, and the sentence names
 * the pair - « texte discret sur fond : 3,2:1 » - because naming it is what
 * tells an operator which control to move.
 */

/* --------------------------------------------------------------- the words */

/** The five self-hosted families of §9.4, as an operator reads them (H24). */
export const FONT_LABEL: Readonly<Record<ExperienceFont, string>> = {
  'titillium-web': 'Titillium Web',
  'source-sans-3': 'Source Sans 3',
  lora: 'Lora',
  'playfair-display': 'Playfair Display',
  caveat: 'Caveat',
};

export const SCALE_LABEL: Readonly<Record<ExperienceScale, string>> = {
  small: 'petit',
  normal: 'normal',
  large: 'grand',
};

/** La RAPIDITÉ de §9.3 - three words, never a duration (H26). */
export const SPEED_LABEL: Readonly<Record<AnimationSpeed, string>> = {
  fast: 'rapide',
  normal: 'normal',
  slow: 'lent',
};

/** The eight entry animations of §9.3. The bar sets the experience-wide default. */
export const BLOCK_ANIMATION_LABEL: Readonly<Record<BlockAnimation, string>> = {
  none: 'aucune',
  fade: 'fondu',
  wipe: 'volet',
  zoom: 'zoom',
  'slide-left': 'glissé depuis la gauche',
  'slide-right': 'glissé depuis la droite',
  'slide-up': 'glissé depuis le bas',
  'slide-down': 'glissé depuis le haut',
};

/** The three transitions of §9.3 - a transition moves a whole page. */
export const PAGE_TRANSITION_LABEL: Readonly<Record<PageTransitionAnimation, string>> = {
  none: 'aucune',
  fade: 'fondu',
  slide: 'glissé',
};

export const BUTTON_SHAPE_LABEL: Readonly<Record<ButtonShape, string>> = {
  square: 'carré',
  rounded: 'arrondi',
  pill: 'pilule',
};

export const BUTTON_STYLE_LABEL: Readonly<Record<ButtonStyle, string>> = {
  outline: 'contour',
  solid: 'plein',
};

/**
 * The eight colours of §9.4, in the order the panel and the pastilles show them.
 *
 * A LIST and not a record: the order is what makes the collapsed row of
 * pastilles readable - ground first, then what sits on it - and a record would
 * leave that order to whoever iterated it.
 */
export const THEME_COLORS = [
  { token: 'background', label: 'fond' },
  { token: 'text', label: 'texte' },
  { token: 'muted', label: 'texte discret' },
  { token: 'accent', label: 'accent' },
  { token: 'buttonBackground', label: 'fond de bouton' },
  { token: 'buttonText', label: 'texte de bouton' },
  { token: 'aiBubble', label: 'bulle de l’IA' },
  { token: 'personBubble', label: 'bulle de la personne' },
] as const satisfies readonly { token: keyof ExperienceTheme; label: string }[];

/* ------------------------------------------------------------ the summaries */

/**
 * The line of the collapsed bar, per group (maquette `Timeline.dc.html`).
 *
 * Every one of them reads the RESOLVED theme and not the partial: the bar
 * collapsed says what an experience LOOKS like, and « Titre : - » on a theme
 * that has simply not overridden the default would be an absence where there is
 * a value. The panel is where the difference between a value and an inherited
 * one is drawn.
 */
export function typographySummary(visual: ResolvedExperienceTheme): string {
  return `Titre : ${FONT_LABEL[visual.titleFont]} · Texte : ${FONT_LABEL[visual.textFont]} · Échelle : ${SCALE_LABEL[visual.textScale]}`;
}

/** « Couleur · voile 0 % », « Image · voile 55 % » (§9.4). */
export function backgroundSummary(visual: ResolvedExperienceTheme): string {
  const kind = visual.backgroundKind === 'image' ? 'Image' : 'Couleur';
  return `${kind} · voile ${veilPercent(visual.backgroundVeil)} %`;
}

/** The veil as an operator reads it: a whole percent, never a float (§9.4). */
export function veilPercent(veil: number): number {
  return Math.round(veil * 100);
}

/** « Pilule · plein » (§9.4). */
export function buttonsSummary(visual: ResolvedExperienceTheme): string {
  return `${capitalise(BUTTON_SHAPE_LABEL[visual.buttonShape])} · ${BUTTON_STYLE_LABEL[visual.buttonStyle]}`;
}

/**
 * « Entrée : fondu · normal · Transition : glissé · normal » (§9.4).
 *
 * In RAPIDITÉ and not in milliseconds (revue du 2026-09-11, seconde passe):
 * `ANIMATION_SPEED_MS` is what the reader plays, three words are what the
 * operator chooses, and the bar speaks the operator's half.
 */
export function motionSummary(visual: ResolvedExperienceTheme): string {
  const entry = `${BLOCK_ANIMATION_LABEL[visual.blockAnimation]} · ${SPEED_LABEL[visual.blockSpeed]}`;
  const between = `${PAGE_TRANSITION_LABEL[visual.pageTransition]} · ${SPEED_LABEL[visual.pageTransitionSpeed]}`;
  return `Entrée : ${entry} · Transition : ${between}`;
}

function capitalise(word: string): string {
  return word.length === 0 ? word : `${word[0]?.toUpperCase() ?? ''}${word.slice(1)}`;
}

/* ------------------------------------------------------------- the contrast */

/** One pairing the theme puts in presence, measured (§9.4). */
export type ThemeContrastPair = {
  /** « texte discret sur fond » - the pair NAMED, which is what makes it actionable. */
  readonly label: string;
  /** The ratio, or null when a colour could not be read (`contrast.ts`). */
  readonly ratio: number | null;
  /** Whether it clears the AA floor for normal text. An unreadable pair is `false`. */
  readonly passes: boolean;
};

/**
 * The five pairings of §9.4, in the order the warning list reads them.
 *
 * « Texte sur fond, texte discret sur fond, texte de bouton sur fond de bouton,
 * texte sur la bulle de l'IA, texte sur la bulle de la personne. » Five and not
 * every combination of eight colours: a pair is what the reader actually paints
 * one over the other, and measuring the accent against the person's bubble
 * would warn about a pairing no page can produce.
 *
 * It takes the RESOLVED theme, so it always has two colours to compare - that
 * totality is exactly what `resolveTheme` promises and why §9.4 can ask for a
 * named pair rather than for a verdict.
 *
 * A background IMAGE changes nothing here, and that is not an oversight: §9.4
 * lays the background COLOUR over the image as a veil precisely « pour garder le
 * texte lisible », and `reader.css` paints it that way. So the pair the reader
 * actually puts in presence is text against that colour, whatever the image
 * shows - and measuring an image would mean measuring a different pixel per
 * word.
 */
export function contrastPairs(visual: ResolvedExperienceTheme): ThemeContrastPair[] {
  const pairs: readonly (readonly [string, string, string])[] = [
    ['texte sur fond', visual.text, visual.background],
    ['texte discret sur fond', visual.muted, visual.background],
    ['texte de bouton sur fond de bouton', visual.buttonText, visual.buttonBackground],
    ['texte sur la bulle de l’IA', visual.text, visual.aiBubble],
    ['texte sur la bulle de la personne', visual.text, visual.personBubble],
  ];
  return pairs.map(([label, front, back]) => {
    const ratio = contrastRatio(front, back);
    return { label, ratio, passes: ratio !== null && ratio >= AA_NORMAL_TEXT };
  });
}

/**
 * One line of the yellow encart: « texte discret sur fond : 3,9:1, sous
 * 4,5:1. » (§9.4).
 *
 * The FLOOR is written into the sentence rather than left implicit, because the
 * number alone means nothing to somebody who has not read WCAG - and this
 * warning is aimed at an operator, not at an auditor.
 */
export function contrastSentence(pair: ThemeContrastPair): string {
  const ratio = formatRatio(pair.ratio);
  if (ratio === null) return `Contraste ${pair.label} : illisible.`;
  return `Contraste ${pair.label} : ${ratio}, sous ${formatRatio(AA_NORMAL_TEXT) ?? ''}.`;
}

/**
 * Every pairing under the floor, worded - what the bar shows and what the save
 * box of §9.1 repeats.
 *
 * « L'avertissement se répète dans la boîte d'enregistrement, parce que c'est le
 * dernier moment où on le lit avant qu'une personne recrutée le voie. » The two
 * surfaces call THIS function rather than each computing: a bar that warned on
 * four pairs and a box that warned on three would teach an operator that one of
 * the two is lying.
 *
 * The argument is the PARTIAL theme, so a caller never has to remember to
 * resolve first - which is the mistake that would silently measure a page's
 * overrides against nothing.
 */
export function contrastWarnings(theme: ExperienceTheme, page?: ExperienceTheme): string[] {
  return contrastPairs(resolveTheme(theme, page))
    .filter((pair) => !pair.passes)
    .map(contrastSentence);
}

/** A page as the contrast reads it: what it is called, and what it overrides. */
export type ContrastedPage = {
  readonly name: string;
  readonly visual: ExperienceTheme;
};

/**
 * What ONE page ADDS to the experience's own warnings, named by that page
 * (§9.4, issue #58).
 *
 * Only what it adds, and that is the point: a page that overrides nothing
 * resolves to the very theme measured just above, so every one of its pairs is
 * already worded - listing them again would repeat each sentence of a four-page
 * experience four times. The filter is on the SENTENCE, which carries the pair
 * and the ratio, so a page that overrides the accent without moving a measured
 * pair adds nothing either.
 *
 * The page's name is PREFIXED rather than the sentence reworded, for the reason
 * §9.4 gives about naming the pair: a warning is actionable when it says which
 * control to move, and at the page level that starts with saying which page.
 */
export function pageContrastWarnings(theme: ExperienceTheme, page: ContrastedPage): string[] {
  const already = new Set(contrastWarnings(theme));
  const named = page.name.trim() === '' ? 'sans nom' : page.name.trim();
  return contrastWarnings(theme, page.visual)
    .filter((sentence) => !already.has(sentence))
    .map((sentence) => `Page « ${named} » · ${sentence}`);
}

/**
 * The experience and every one of its pages, measured together - what BOTH save
 * boxes repeat (§9.1, §9.4, issue #58).
 *
 * One function and two callers, the arbitrage `contrastWarnings` already
 * states: the box of the shell and the box of the page editor record the SAME
 * version, so a box warning about four pairs beside one warning about three
 * would teach an operator that one of the two is lying. The page editor is not
 * handed a shorter list because it happens to show one page: what is about to
 * be recorded is the whole experience.
 */
export function experienceContrastWarnings(
  theme: ExperienceTheme,
  pages: readonly ContrastedPage[],
): string[] {
  return [
    ...contrastWarnings(theme),
    ...pages.flatMap((page) => pageContrastWarnings(theme, page)),
  ];
}

/* --------------------------------------------------------------- the levels */

/**
 * Which level a token comes from (§9.4, the panel's « hérité » / « surchargé »).
 *
 * Not derivable from a resolved theme, which is the whole point: the merge
 * forgets where a value came from, and the panel's entire job at the page level
 * is to say it. The answer is about the KEY and never about the value - a page
 * that overrides the background with the very colour the theme already carries
 * is still overriding it, and « Revenir au global » still has something to
 * erase.
 */
export function isOverridden(value: ExperienceTheme, token: keyof ExperienceTheme): boolean {
  return value[token] !== undefined;
}

/** Whether this level overrides anything of a group - the « surchargé » pastille. */
export function groupOverridden(
  value: ExperienceTheme,
  tokens: readonly (keyof ExperienceTheme)[],
): boolean {
  return tokens.some((token) => isOverridden(value, token));
}

/**
 * One token written at this level.
 *
 * `undefined` ERASES the key rather than storing it, and the two are not the
 * same thing under `exactOptionalPropertyTypes`: a stored `{ accent: undefined }`
 * survives `experienceThemeSchema` and then spreads `undefined` over the level
 * below in `resolveTheme` - which `shared`'s own `defined()` exists to guard
 * against. Erasing here means the guard never has to fire.
 */
export function withToken<Token extends keyof ExperienceTheme>(
  value: ExperienceTheme,
  token: Token,
  next: ExperienceTheme[Token] | undefined,
): ExperienceTheme {
  const written: ExperienceTheme = { ...value };
  if (next === undefined) delete written[token];
  else written[token] = next;
  return written;
}

/**
 * « Revenir au global » : every override of this level erased (§9.4).
 *
 * An empty object and not a theme filled with the inherited values: an absent
 * token INHERITS, so a page that copied the experience's colours would keep
 * them the day somebody changes the experience - which is the opposite of what
 * the button says.
 */
export function clearedTheme(): ExperienceTheme {
  return {};
}

/**
 * What a level's controls SHOW: the value in force, resolved.
 *
 * At `global` the level below is the PMS default; at `page` it is the
 * experience's theme. Both are `resolveTheme` with one argument shifted, so
 * there is one merge in the repository and this is a naming of its two uses.
 */
export function themeInForce(
  value: ExperienceTheme,
  inherited: ExperienceTheme | undefined,
): ResolvedExperienceTheme {
  return resolveTheme(inherited, value);
}
